// src/pages/Annualreport.jsx
// Vista pública

import { useEffect, useMemo, useState } from "react";
import { Banner } from "../../../components/common/Banner";
import { Calendar, Clock3, ArrowRightCircle } from "lucide-react";
import * as Svc from "../../../services/annualreportService"; // ajusta si tu path es distinto

/* ===== Utils ===== */
const formatMB = (bytes) =>
  typeof bytes === "number" && !Number.isNaN(bytes)
    ? (bytes / (1024 * 1024)).toFixed(2)
    : undefined;

const absUrl = (href) => {
  try {
    return new URL(href, window.location.origin).href;
  } catch {
    return href;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const safePos = (r) => {
  const p = Number(r?.position);
  return Number.isInteger(p) && p > 0 ? p : Number.POSITIVE_INFINITY;
};
const cmpInYear = (a, b) => {
  const pa = safePos(a),
    pb = safePos(b);
  if (pa !== pb) return pa - pb;
  const ca = a?.createdAt ? +new Date(a.createdAt) : 0;
  const cb = b?.createdAt ? +new Date(b.createdAt) : 0;
  if (ca !== cb) return ca - cb;
  return (a?.title || "")
    .toLowerCase()
    .localeCompare((b?.title || "").toLowerCase());
};

const PdfIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
      stroke="#0B4A8F"
      strokeWidth="1.6"
    />
    <path d="M14 2v4h4" stroke="#0B4A8F" strokeWidth="1.6" />
    <text x="7.5" y="16" fontSize="7" fontWeight="700" fill="#0B4A8F">
      PDF
    </text>
  </svg>
);

/* ===== Row de enlace ===== */
function LinkRow({ report }) {
  const href =
    report.secureUrl ||
    (Svc.downloadAnnualReportUrl
      ? Svc.downloadAnnualReportUrl(report.id)
      : report.secureUrl);
  const url = absUrl(href);
  const mb = formatMB(report.bytes);
  const label = report.title?.trim() || `Reporte anual ${report.year}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between gap-3 text-sm"
      title={label}
    >
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 border border-blue-100 group-hover:bg-[#0B4A8F] group-hover:border-[#0B4A8F] transition-colors">
          <PdfIcon size={16} />
        </span>
        <span className="font-semibold text-[#0B4A8F] group-hover:underline truncate max-w-[220px] sm:max-w-[260px]">
          {label}
        </span>
        {report.version && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-gray-100 border text-gray-700">
            v{report.version}
          </span>
        )}
        {mb && <span className="text-xs text-gray-500">({mb} MB)</span>}
      </div>

      <ArrowRightCircle className="w-4 h-4 text-gray-300 group-hover:text-[#0B4A8F] flex-shrink-0" />
    </a>
  );
}

export default function Annualreport() {
  const thisYear = new Date().getFullYear();
  const MIN_YEAR = 1976;

  const [filterYear, setFilterYear] = useState("");
  const [items, setItems] = useState([]);
  const [featuredHeadline, setFeaturedHeadline] = useState("");

  // Textos + CTA + última visible
  const [pageTitle, setPageTitle] = useState("Cuenta pública");
  const [subtitle, setSubtitle] = useState("Descarga las cuenta pública por año.");
  const [previousTitle, setPreviousTitle] = useState("REPORTES ANTERIORES");

  const [ctaEnabled, setCtaEnabled] = useState(false);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [lastVisibleAt, setLastVisibleAt] = useState(null);

  const yearEnd = thisYear + 1;
  const yearOptions = Array.from(
    { length: yearEnd - MIN_YEAR + 1 },
    (_, i) => yearEnd - i
  );

  /* ===== Config pública ===== */
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cfg = await Svc.getAnnualReportsConfig();
        if (!active) return;
        setPageTitle(cfg?.pageTitle ?? "Cuenta pública");
        setSubtitle(cfg?.subtitle ?? "Descarga las cuenta pública por año.");
        setPreviousTitle(cfg?.previousTitle ?? "REPORTES ANTERIORES");
        setCtaEnabled(!!cfg?.ctaEnabled);
        setCtaLabel(cfg?.ctaLabel || "");
        setCtaUrl(cfg?.ctaUrl || "");
        setLastVisibleAt(cfg?.lastVisibleAt || null);
      } catch {
        /* noop */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  /* ===== Lista pública ===== */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const y = filterYear ? Number(filterYear) : undefined;
        let data;
        if (typeof Svc.listAnnualReportsPublic === "function") {
          data = await Svc.listAnnualReportsPublic(y);
        } else {
          // fallback: usa lista completa y filtra visibles
          data = await Svc.listAnnualReports(y);
          data = (Array.isArray(data) ? data : []).filter(
            (r) => r.visibleForMembers !== false
          );
        }
        if (!mounted) return;
        setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!mounted) return;
        setItems([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [filterYear]);

  /* ===== Agrupar por año ===== */
  const grouped = useMemo(() => {
    const g = items.reduce((acc, it) => {
      (acc[it.year] ||= []).push(it);
      return acc;
    }, /** @type {Record<number, any[]>} */ ({}));
    Object.keys(g).forEach((k) => {
      g[k] = [...g[k]].sort(cmpInYear);
    });
    return g;
  }, [items]);

  const yearsDesc = useMemo(
    () => Object.keys(grouped).map(Number).sort((a, b) => b - a),
    [grouped]
  );

  const featuredYear = filterYear ? null : yearsDesc[0];
  const featuredList = featuredYear ? grouped[featuredYear] || [] : [];

  /* ===== Título del año destacado ===== */
  useEffect(() => {
    let active = true;
    (async () => {
      if (!featuredYear) {
        setFeaturedHeadline("");
        return;
      }
      try {
        const res = await Svc.getAnnualReportYearTitle(featuredYear);
        if (!active) return;
        setFeaturedHeadline((res?.headline || "").trim());
      } catch {
        if (!active) return;
        setFeaturedHeadline("");
      }
    })();
    return () => {
      active = false;
    };
  }, [featuredYear]);

  const yearsToShow = filterYear
    ? yearsDesc
    : yearsDesc.filter((y) => y !== featuredYear);

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <Banner title={pageTitle} message={subtitle} className="mb-6" />

      {/* Panel superior: CTA + última actualización + filtro */}
      <div className="rounded-2xl bg-gradient-to-r from-[#E6F0FF] via-[#F3FAFF] to-[#E8F9FF] p-4 md:p-5 border border-blue-100/70 mb-8">
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-5">
          <div className="flex flex-wrap items-center gap-3">
            {/* Última actualización */}
            <div className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white/80 px-3.5 py-2 rounded-xl border border-gray-200 shadow-sm">
              <Clock3 className="w-4 h-4 text-[#0B4A8F]" />
              <span className="font-medium">Última actualización</span>
              <span className="font-semibold text-gray-900">
                {fmtDate(lastVisibleAt)}
              </span>
            </div>

            {/* CTA */}
            {ctaEnabled && ctaLabel && ctaUrl && (
              <a
                href={ctaUrl}
                target={ctaUrl.startsWith("http") ? "_blank" : undefined}
                rel={
                  ctaUrl.startsWith("http")
                    ? "noopener noreferrer"
                    : undefined
                }
                className="inline-flex items-center gap-2 rounded-full bg-[#0B4A8F] hover:bg-[#093a70] text-white px-4 py-2 text-sm shadow-sm"
              >
                {ctaLabel}
                <ArrowRightCircle className="w-4 h-4" />
              </a>
            )}
          </div>

          {/* Separador visual */}
          <div className="hidden md:block w-px h-8 bg-gray-300" />

          {/* Filtro por año */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 whitespace-nowrap">
              <Calendar className="w-4 h-4 text-[#0B4A8F]" />
              Filtrar por año
            </div>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="w-[180px] px-3 py-2 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm"
            >
              <option value="">Todos los años</option>
              {(yearsDesc.length ? yearsDesc : yearOptions).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            {filterYear && (
              <button
                type="button"
                onClick={() => setFilterYear("")}
                className="text-xs text-[#0488AF] hover:underline whitespace-nowrap"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* HERO último año */}
      {!filterYear && featuredYear && featuredList.length > 0 && (
        <>
          {/* ⬇⬇ aquí cambié el degradado a abajo → arriba */}
          <div className="rounded-3xl bg-gradient-to-t from-[#0EC5DF] via-[#0488AF] to-[#0B4A8F] text-white px-5 py-6 md:px-7 md:py-7 mb-10 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-baseline gap-3">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-white/70">
                  Informe reciente
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-white/15 border border-white/30">
                  {featuredYear}
                </span>
              </div>
            </div>

            <div className="grid gap-5 md:gap-7 md:grid-cols-[minmax(240px,280px),minmax(0,1fr)] items-start">
              {/* Portada */}
              <div className="w-full max-w-[280px] justify-self-center md:justify-self-start">
                <div className="aspect-[3/4] rounded-2xl bg-white overflow-hidden shadow-md">
                  <img
                    className="w-full h-full object-cover"
                    src={featuredList[0].coverUrl}
                    alt={`Informe Anual ${featuredYear}`}
                    onError={(e) => {
                      const href = absUrl(
                        featuredList[0].secureUrl ||
                          (Svc.downloadAnnualReportUrl
                            ? Svc.downloadAnnualReportUrl(
                                featuredList[0].id
                              )
                            : featuredList[0].secureUrl)
                      );
                      e.currentTarget.parentElement.innerHTML = `<embed type="application/pdf" src="${href}#page=1&zoom=125" style="width:100%;height:100%;display:block;border:0" />`;
                    }}
                  />
                </div>
              </div>

              {/* Título + lista */}
              <div className="w-full min-w-0">
                <div className="text-xl md:text-2xl font-semibold mb-3">
                  {featuredHeadline || `Informe Anual ${featuredYear}`}
                </div>
                <div className="space-y-2.5">
                  {featuredList.map((rep) => (
                    <div
                      key={rep.id}
                      className="rounded-xl bg-white/95 text-gray-900 px-3 py-2 shadow-sm"
                    >
                      <LinkRow report={rep} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {yearsDesc.length > 1 && (
            <div className="mb-4">
              <div className="text-sm font-semibold tracking-[0.18em] text-[#C42251] mb-1 uppercase">
                {previousTitle?.trim() || "REPORTES ANTERIORES"}
              </div>
              <hr className="border-gray-200" />
            </div>
          )}
        </>
      )}

      {/* Reportes anteriores (o todos si hay filtro) */}
      {yearsToShow.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {yearsToShow.map((year) => {
            const yearItems = grouped[year] || [];
            if (yearItems.length === 0) return null;

            const coverItem = yearItems[0];
            const coverHref = absUrl(
              coverItem.secureUrl ||
                (Svc.downloadAnnualReportUrl
                  ? Svc.downloadAnnualReportUrl(coverItem.id)
                  : coverItem.secureUrl)
            );

            return (
              <div
                className="rounded-2xl bg-white ring-1 ring-black/5 p-4 flex flex-col gap-3 shadow-sm"
                key={year}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-base md:text-lg font-bold text-gray-800">
                    {year}
                  </div>
                </div>

                {/* Portada centrada */}
                <div className="w-full flex justify-center">
                  <div className="w-full max-w-[220px]">
                    <div className="aspect-[3/4] border rounded-xl overflow-hidden bg-gray-50">
                      <img
                        className="w-full h-full object-cover"
                        src={coverItem.coverUrl}
                        alt={`Portada ${year}`}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.parentElement.innerHTML = `<embed type="application/pdf" src="${coverHref}#page=1&zoom=115" style="width:100%;height:100%;display:block;border:0" />`;
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-1">
                  {yearItems.map((rep) => (
                    <div
                      key={rep.id}
                      className="rounded-xl border border-gray-200/70 bg-white/80 px-3 py-2"
                    >
                      <LinkRow report={rep} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {yearsDesc.length === 0 && (
        <div className="text-gray-500 mt-6 text-sm">
          No hay reportes disponibles por ahora.
        </div>
      )}
    </div>
  );
}
